// textSanitisers.js
//
// Field-aware text sanitisation for patient form answers. Applied at display
// and prefill layers — raw formData in Firestore is never mutated.
//
// IMPORTANT: This field list is mirrored in python-functions/functions/text_sanitisation.py.
// If you add or change a field here, update the Python counterpart too.

/**
 * Capitalises the first letter of each word in a string. Designed for
 * address / location / institutional fields, not personal names (those have
 * too many cultural edge-cases like "de Beers", "McDonald", "O'Brien").
 *
 * Behaviour:
 *  - Splits on whitespace, capitalises the first char of each token.
 *  - Handles hyphenated tokens: "fleet-street" → "Fleet-Street".
 *  - Leaves fully-uppercase tokens untouched: "BBC" stays "BBC".
 *  - Leaves tokens that start with a digit untouched: "123a" stays "123a".
 *  - Collapses multiple spaces into one.
 */
export const toTitleCase = (str) => {
  if (!str || typeof str !== 'string') return str;

  return str
    .trim()
    .split(/\s+/)
    .map((word) => {
      // Already fully uppercase (e.g. "BBC", "NW1") — leave as-is
      if (word === word.toUpperCase() && /[A-Z]/.test(word)) return word;

      // Starts with a digit — leave as-is (e.g. "123", "2nd")
      if (/^\d/.test(word)) return word;

      // Handle hyphenated words: capitalise each part independently
      if (word.includes('-')) {
        return word
          .split('-')
          .map((part) => {
            if (!part) return part;
            if (part === part.toUpperCase() && /[A-Z]/.test(part)) return part;
            if (/^\d/.test(part)) return part;
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
          })
          .join('-');
      }

      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

/**
 * Formats a UK postcode into its canonical form: uppercase with a single
 * space separating the outcode from the incode (the last 3 characters).
 *
 *  "sw1a1aa"   → "SW1A 1AA"
 *  "SW1A 1AA"  → "SW1A 1AA"  (idempotent)
 *  "ec1a 1bb"  → "EC1A 1BB"
 *
 * If the stripped length falls outside the valid 5–7 character range for UK
 * postcodes, we just uppercase and trim as a safe fallback.
 */
export const formatUKPostcode = (v) => {
  if (!v || typeof v !== 'string') return v;
  const stripped = v.replace(/\s+/g, '').toUpperCase();
  if (stripped.length < 5 || stripped.length > 7) return v.toUpperCase().trim();
  return `${stripped.slice(0, -3)} ${stripped.slice(-3)}`;
};

/**
 * Finds a UK postcode pattern embedded in a longer string (e.g. an address
 * line that includes both a street and postcode) and uppercases it.
 *
 * Applied after toTitleCase so that "14 crescent road, rh2 h12" first
 * becomes "14 Crescent Road, Rh2 H12" and then the postcode portion is
 * corrected to "RH2 H12".
 *
 * UK postcode anatomy:  outcode (1-2 letters + digit + optional letter/digit)
 *                       + space + incode (digit + 2 letters).
 * The regex uses word boundaries to avoid false positives on normal words.
 */
export const uppercaseEmbeddedPostcode = (str) => {
  if (!str || typeof str !== 'string') return str;
  // UK postcode structure (per Royal Mail spec):
  //   Outcode (2-4 chars): 1-2 letters + 1-2 digits + optional trailing letter
  //     Patterns: A9, A99, A9A, AA9, AA99, AA9A  (e.g. M1, RH10, SW1A, EC1A)
  //   Incode  (3 chars):  always digit + 2 letters  (e.g. 0NY, 7GZ, 7HF)

  // Pass 1: postcodes with a space between outcode and incode (e.g. "rh1 7hf")
  let result = str.replace(
    /\b([A-Za-z]{1,2}\d{1,2}[A-Za-z]?)\s+(\d[A-Za-z]{2})\b/g,
    (_, outcode, incode) => `${outcode.toUpperCase()} ${incode.toUpperCase()}`
  );

  // Pass 2: postcodes with NO space (e.g. "sw4a7td" → after title-case "Sw4a7td").
  // Detects the concatenated outcode+incode, uppercases, and inserts the canonical space.
  result = result.replace(
    /\b([A-Za-z]{1,2}\d{1,2}[A-Za-z]?)(\d[A-Za-z]{2})\b/g,
    (_, outcode, incode) => `${outcode.toUpperCase()} ${incode.toUpperCase()}`
  );

  return result;
};

/**
 * Uppercases only the first character of each word (and each hyphenated part),
 * preserving the rest of the casing exactly as the user typed it. Designed for
 * personal name fields where we want a minimal, safe tidy-up without making
 * assumptions about internal casing conventions.
 *
 * Unlike toTitleCase (which lowercases the rest of each token), this function
 * never changes any character beyond position 0 of each token — so "mcdonald"
 * becomes "Mcdonald" and "O'Brien" stays "O'Brien".
 *
 * Behaviour:
 *  - Splits on whitespace, then on hyphens within each word.
 *  - Uppercases the first character of every resulting token.
 *  - Preserves the rest of each token's casing.
 *  - Trims leading/trailing whitespace and collapses multiple spaces.
 */
export const capitaliseName = (str) => {
  if (!str || typeof str !== 'string') return str;

  return str
    .trim()
    .split(/\s+/)
    .map((word) => {
      if (word.includes('-')) {
        return word
          .split('-')
          .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
          .join('-');
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

/**
 * Maps serviceUserVarName → sanitisation function. Only fields that benefit
 * from automatic formatting are included.
 *
 * Name fields (firstName, middleName, lastName, gpDoctorName) use
 * capitaliseName — a light touch that only uppercases the first character of
 * each word/hyphenated part, preserving the rest of the casing.
 *
 * Address / location / institutional fields use toTitleCase which also
 * lowercases the remainder of each token.
 */
export const fieldSanitisers = {
  firstName:        (v) => capitaliseName(v),
  middleName:       (v) => capitaliseName(v),
  lastName:         (v) => capitaliseName(v),
  gpDoctorName:     (v) => capitaliseName(v),
  streetAddress:    (v) => toTitleCase(v),
  city:             (v) => toTitleCase(v),
  postcode:         (v) => formatUKPostcode(v),
  gpSurgeryName:    (v) => toTitleCase(v),
  // GP surgery address is a single freeform field where patients enter both
  // street and postcode together. Title-case the text first, then detect and
  // uppercase any embedded UK postcode so "rh2 h12" → "RH2 H12".
  gpSurgeryAddress: (v) => uppercaseEmbeddedPostcode(toTitleCase(v)),
  nhsNumber:        (v) => (v ? v.replace(/\D/g, '') : v),
};

/**
 * Applies the appropriate field sanitiser (if one exists) to a value.
 *
 * @param {string} serviceUserVarName  The field identifier from the form data model.
 * @param {*} value                    The raw patient-entered value.
 * @returns {{ sanitisedValue: *, wasSanitised: boolean }}
 *   sanitisedValue — the cleaned value (or the original if no sanitiser matched).
 *   wasSanitised   — true only when the sanitiser actually changed the value.
 */
export const applySanitiser = (serviceUserVarName, value) => {
  if (!serviceUserVarName || typeof value !== 'string' || !value) {
    return { sanitisedValue: value, wasSanitised: false };
  }

  const sanitiser = fieldSanitisers[serviceUserVarName];
  if (!sanitiser) {
    return { sanitisedValue: value, wasSanitised: false };
  }

  const sanitisedValue = sanitiser(value);
  return {
    sanitisedValue,
    wasSanitised: sanitisedValue !== value,
  };
};
